import { useCallback, useEffect, useState } from 'react';
import { listIngestChannelFiles as defaultListIngestChannelFiles } from '../../api/originatorApi';

function formatModified(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function InboxFilePicker({
  channel,
  onAddFiles,
  disabled,
  selectedNames = new Set(),
  listFiles = defaultListIngestChannelFiles,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inboxPath, setInboxPath] = useState('');
  const [items, setItems] = useState([]);
  const [picked, setPicked] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await listFiles(channel);
      setInboxPath(response.inbox_path || '');
      setItems(Array.isArray(response.items) ? response.items : []);
    } catch (requestError) {
      setItems([]);
      setError(requestError.message || 'Could not list inbox files.');
    } finally {
      setLoading(false);
    }
  }, [channel, listFiles]);

  useEffect(() => {
    setPicked(new Set());
    load();
  }, [load]);

  function toggleName(name) {
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  function handleAdd() {
    const chosen = items.filter((row) => picked.has(row.name) && row.importable);
    if (!chosen.length) return;
    onAddFiles(
      chosen.map((row) => ({
        name: row.name,
        size_bytes: row.size_bytes,
        channel,
      }))
    );
    setPicked(new Set());
  }

  const selectable = items.filter((row) => row.importable && !selectedNames.has(row.name));

  return (
    <div className="rounded-xl border border-eorig-border bg-white px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-eorig-ink">Inbox files</h3>
          {inboxPath ? (
            <p className="mt-0.5 font-mono text-[11px] text-eorig-muted">{inboxPath}</p>
          ) : null}
        </div>
        <button
          type="button"
          className="text-[12px] font-semibold text-eorig-navy-600 hover:text-eorig-cyan disabled:opacity-40"
          onClick={load}
          disabled={disabled || loading}
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <p className="mt-3 text-[13px] text-eorig-muted">Loading inbox…</p>
      ) : error ? (
        <p className="mt-3 text-[13px] text-rose-600" role="alert">
          {error}
        </p>
      ) : !items.length ? (
        <p className="mt-3 text-[13px] text-eorig-muted">
          No files in this inbox yet. Drop PDF or ZIP files into the folder above, then refresh.
        </p>
      ) : (
        <div className="mt-3 max-h-64 overflow-y-auto rounded-lg border border-eorig-border">
          <table className="min-w-full text-left text-[12px]">
            <thead className="sticky top-0 bg-eorig-surface text-[11px] uppercase text-eorig-muted">
              <tr>
                <th className="px-2 py-1.5 font-semibold">Select</th>
                <th className="px-2 py-1.5 font-semibold">File</th>
                <th className="px-2 py-1.5 font-semibold">Size</th>
                <th className="px-2 py-1.5 font-semibold">Modified</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => {
                const queued = selectedNames.has(row.name);
                const canPick = row.importable && !queued;
                return (
                  <tr key={row.name} className="border-t border-eorig-border">
                    <td className="px-2 py-1.5">
                      <input
                        type="checkbox"
                        checked={picked.has(row.name)}
                        disabled={disabled || !canPick}
                        onChange={() => toggleName(row.name)}
                        aria-label={`Select ${row.name}`}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <span className={row.importable ? 'text-eorig-ink' : 'text-eorig-muted'}>
                        {row.name}
                      </span>
                      {queued ? (
                        <span className="ml-1 text-[11px] text-eorig-muted">(queued)</span>
                      ) : null}
                      {!row.importable && row.reason ? (
                        <span className="mt-0.5 block text-[11px] text-rose-600">{row.reason}</span>
                      ) : null}
                    </td>
                    <td className="px-2 py-1.5 text-eorig-muted">{row.size_bytes ?? '—'}</td>
                    <td className="px-2 py-1.5 text-eorig-muted">{formatModified(row.modified_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3 flex justify-end">
        <button
          type="button"
          className="rounded-lg bg-eorig-navy-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
          disabled={disabled || loading || !picked.size}
          onClick={handleAdd}
        >
          Add selected to package
        </button>
      </div>
      {selectable.length === 0 && items.length > 0 && !loading && !error ? (
        <p className="mt-2 text-[12px] text-eorig-muted">
          All importable inbox files are already queued, or none pass the type/size rules.
        </p>
      ) : null}
    </div>
  );
}
